import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from '@mui/material';
import { PersonOff as EmptyIcon } from '@mui/icons-material';
import getContactsFromCustomer from 'api/contacts/getContactsFromCustomer';

interface ContactRecord {
  id: string;
  firstName: string;
  lastName: string;
  phoneWork: string;
  phoneMobile: string;
  emailWork: string;
}

interface ContactsTabProps {
  customerId: number;
}

export function ContactsTabSkeleton() {
  return (
    <Box sx={{ p: 2 }}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {['Name', 'Phone', 'Email'].map((h) => (
                <TableCell key={h}>
                  <Skeleton width={60} height={18} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(3)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(3)].map((__, j) => (
                  <TableCell key={j}>
                    <Skeleton width={j === 2 ? 120 : 80} height={18} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function ContactsTab({ customerId }: ContactsTabProps) {
  const [contacts, setContacts] = useState<ContactRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getContactsFromCustomer(customerId);
      setContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Failed to load contacts. Please try again.');
      console.error('[ContactsTab] Fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  if (loading) {
    return <ContactsTabSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      </Box>
    );
  }

  if (contacts.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <EmptyIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary" variant="body2">
          No contacts found for this customer
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <TableContainer>
        <Table size="small" aria-label="Customer contacts">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Name
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', display: { xs: 'none', sm: 'table-cell' } }}>
                Phone
              </TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Email
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow
                key={contact.id}
                hover
                sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {[contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Unnamed'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  <Typography variant="body2" color="text.secondary">
                    {contact.phoneWork || contact.phoneMobile || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 180 }}>
                    {contact.emailWork || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
